import FilterListIcon from "@mui/icons-material/FilterList";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    getEventBackgroundColor,
    getEventColorBasedOnStatus,
} from "crqlar-react-common/dist/utils/color.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";

const STATUS_ORDER: readonly ReservationStatus[] = [
    ReservationStatus.PENDING,
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
    ReservationStatus.NO_SHOW,
    ReservationStatus.CANCELLED,
    ReservationStatus.OPTION,
    ReservationStatus.UNCOMPLETED,
];

export interface ReservationStatusPickerProps {
    availableStatuses: readonly ReservationStatus[];
    selectedStatuses: readonly ReservationStatus[];
    onSelectedStatusesChange: (statuses: ReservationStatus[]) => void;
}

const rowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
    "& .MuiFormControlLabel-label": {flex: 1, minWidth: 0},
});

export function ReservationStatusPicker({
    availableStatuses,
    selectedStatuses,
    onSelectedStatusesChange,
}: ReservationStatusPickerProps) {
    const {t} = useTranslation();
    const orderedStatuses = useMemo(() => {
        const rank = (status: ReservationStatus) => {
            const index = STATUS_ORDER.indexOf(status);
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        };

        return Array.from(new Set(availableStatuses)).sort((left, right) => rank(left) - rank(right));
    }, [availableStatuses]);
    const selectedStatusSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);
    const allSelected = orderedStatuses.length > 0
        && orderedStatuses.every(status => selectedStatusSet.has(status));

    const toggleStatus = (status: ReservationStatus) => {
        const nextStatuses = new Set(selectedStatusSet);

        if (nextStatuses.has(status)) {
            nextStatuses.delete(status);
        } else {
            nextStatuses.add(status);
        }

        onSelectedStatusesChange(orderedStatuses.filter(value => nextStatuses.has(value)));
    };

    return (
        <RightSidebarPanelButton
            icon={(
                <Badge
                    badgeContent={selectedStatusSet.size}
                >
                    <FilterListIcon/>
                </Badge>
            )}
            label={t("Reservation status")}
            disabled={orderedStatuses.length === 0}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader title={t("Reservation status")} onClose={close}/>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        sx={{px: 2, pb: 1}}
                    >
                        <Button
                            type="button"
                            size="small"
                            color="info"
                            onClick={() => onSelectedStatusesChange(allSelected ? [] : [...orderedStatuses])}
                        >
                            {allSelected ? t("Deselect all") : t("Select all")}
                        </Button>
                    </Stack>

                    <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2, gap: 0.25}}>
                        {orderedStatuses.map(status => {
                            const selected = selectedStatusSet.has(status);

                            return (
                                <FormControlLabel
                                    key={status}
                                    control={(
                                        <Checkbox
                                            checked={selected}
                                            onChange={() => toggleStatus(status)}
                                            size="small"
                                            sx={{p: 0.5, mr: 0.5}}
                                        />
                                    )}
                                    label={(
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                            sx={{width: "100%", minWidth: 0, pr: 1}}
                                        >
                                            <Typography noWrap>{t(`Reservation status ${status}`)}</Typography>
                                            <Box
                                                component="span"
                                                aria-hidden
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    flexShrink: 0,
                                                    borderRadius: "50%",
                                                    boxSizing: "border-box",
                                                    bgcolor: getEventBackgroundColor(status),
                                                    border: "1px solid",
                                                    borderColor: getEventColorBasedOnStatus(status),
                                                }}
                                            />
                                        </Stack>
                                    )}
                                    sx={rowSx(selected)}
                                />
                            );
                        })}
                    </Stack>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
